// React
import React, {useEffect, useState, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';

// Third Party
import {inject, observer} from 'mobx-react';

// MUI
import {Select, Typography, Stack} from '@wellstone-solutions/web';

import {BridgeEventSources, Events} from '@wellstone-solutions/common';
import {useEventSource} from 'hooks/useEventSource';
import {AccessControl} from 'modules/rbac';
import {GroupMemberList} from 'modules/group';
import Link from '../../components/tutorial/link';
import {LoadingIndicator} from '../../components/shared/LoadingIndicator';
import Activity from '../../components/group/detail/Activity';
import {Header} from 'modules/group/components/GroupDetail';
import {groupsQueryOptions, groupsEventsQueryOptions} from 'queries';

import type {AppUIStore} from '../../stores/appUIStore';
import type {EventStore} from '../../stores/eventStore';
import type {MeStore} from '../../stores/meStore';
import type {RBACStore} from '../../stores/rbacStore';

type PropsType = {
  appUIStore: AppUIStore,
  eventStore: EventStore,
  meStore: MeStore,
  RBACStore: RBACStore,
  memberNotifications: any,
};

const GroupDetail = (props: PropsType) => {
  const params = useParams();
  useEventSource(BridgeEventSources.GROUP_DETAIL_PAGE);

  const {appUIStore, eventStore, meStore, memberNotifications, RBACStore} =
    props;
  const org = meStore.me.membership.organization.id;
  const groupId = params.group;

  const [activeTab, setActiveTab] = useState('members');
  const [memberFilter, setMemberFilter] = useState('active');
  const groupMemberListRef = useRef();

  const groupMemberListRefetch = () => {
    groupMemberListRef.current?.refetch();
  };

  const groupQuery = useQuery(groupsQueryOptions.detail(groupId));
  const groupEventsQuery = useQuery(groupsEventsQueryOptions.list(groupId));

  const finishedLoading =
    !groupQuery.isLoading &&
    !groupEventsQuery.isLoading &&
    !groupEventsQuery.isFetching;

  useEffect(() => {
    appUIStore.setBreadcrumbs([]);

    eventStore.addEvent(Events.VIEWED_GROUP_DETAIL, {
      group_id: groupId,
    });
  }, [appUIStore, groupId, org]);

  if (finishedLoading && !groupQuery.data) {
    return (
      <Typography variant="h4" sx={{fontWeight: 300, color: '#444'}}>
        Group does not exist, or you may not have access
      </Typography>
    );
  }

  if (!finishedLoading) {
    return <LoadingIndicator />;
  }

  const handleMemberFilterChange = (event) => {
    setMemberFilter(event.target.value);
  };

  return (
    <div>
      <Header
        group={groupQuery.data}
        groupEvents={groupEventsQuery.data}
        groupMemberListRefetch={groupMemberListRefetch}
      />
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
        sx={{mb: 2}}></Stack>
      <div className="tabs">
        <ul>
          <li className={activeTab === 'members' ? 'is-active' : ''}>
            <Link
              onClick={() => setActiveTab('members')}
              id="tab-group-detail-members">
              Members
            </Link>
          </li>
          <li className={activeTab === 'activity' ? 'is-active' : ''}>
            <Link
              onClick={() => setActiveTab('activity')}
              id="tab-member-detail-activity">
              Activity
            </Link>
          </li>
        </ul>
      </div>
      {activeTab === 'members' && (
        <div>
          {RBACStore.hasAccess(AccessControl.staff.viewDischargedMembers) && (
            <Select
              data-testid="active-discharge-dropdown"
              value={memberFilter}
              style={styles.listFilter}
              onChange={handleMemberFilterChange}
              items={[
                {label: 'Active Enrollments in this Group', value: 'active'},
                {label: 'Discharged from this Group', value: 'discharged'},
              ]}
              canUnselect={false}
            />
          )}

          <GroupMemberList
            groupId={groupId}
            filter={memberFilter}
            dataGridRef={groupMemberListRef}
          />
        </div>
      )}
      {activeTab === 'activity' && (
        <Activity
          org={org}
          groupId={groupId}
          memberNotifications={memberNotifications}
        />
      )}
    </div>
  );
};

const styles = {
  bold: {fontWeight: 'bold'},
  listFilter: {
    marginBottom: 20,
  },
  modalContent: {
    overflowY: 'hidden',
  },
  statColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fieldLabel: {
    paddingRight: 10,
  },
  stackedLevelItems: {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexDirection: 'column',
  },
  stackedLevelItem: {
    marginBottom: 10,
  },
  textLeft: {
    justifyContent: 'start',
  },
  textRight: {
    justifyContent: 'flex-end',
  },
  whiteLink: {
    color: 'white',
  },
};

export default inject(
  'appUIStore',
  'eventStore',
  'meStore',
  'RBACStore',
)(observer(GroupDetail));
